/**
 * The rail's destination list. Two scopes, one component.
 *
 * Order is fixed and never personalized: muscle memory is worth more than
 * relevance ranking in a nine item list. Badges arrive in M4 and only ever
 * mean "come here", never "there are things here".
 *
 * TWO ENTRIES ARE CONDITIONAL, and neither is personalization. Add-ons is
 * hidden outright, there is nothing to offer yet and a door onto an empty
 * room is worse than no door. And while a project is still PROVISIONING
 * every destination whose whole content is derived from a running app is
 * hidden, because none of them can say anything true yet: there is no
 * database to browse, no files, no traffic, no usage and no activity until
 * the run that is building them settles. Showing them meant a person poking
 * at five empty screens and concluding the product was broken.
 */

import { NavLink } from 'react-router';
import { useAccount, useProjects } from '../../lib/auth/use-app-data';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  /** data-tour value, for the two rail items the first run tour points at. */
  tour?: string;
}

function projectItems(slug: string, provisioning: boolean): NavItem[][] {
  const base = `/p/${encodeURIComponent(slug)}`;
  const main: NavItem[] = [{ to: base, label: t('Overview'), end: true }];

  // Everything below Overview reads something the provision run has not
  // finished creating. Overview stays, because it is where the progress is.
  if (!provisioning) {
    main.push(
      { to: `${base}/deploys`, label: t('Deploys') },
      { to: `${base}/database`, label: t('Database') },
      { to: `${base}/files`, label: t('Files') },
      { to: `${base}/keys`, label: t('Keys and settings') },
      { to: `${base}/activity`, label: t('Activity') },
      // Between Activity and Usage on purpose: what went wrong, then who came
      // and what they got, then what it cost against the plan. Inserting here
      // disturbs exactly one existing adjacency; anywhere else disturbs two.
      { to: `${base}/traffic`, label: t('Traffic') },
      { to: `${base}/usage`, label: t('Usage') },
    );
  }

  return [main, [{ to: `${base}/settings`, label: t('Settings') }]];
}

/**
 * `isAdmin` is the first conditional item this list has ever had - every
 * other entry is unconditional, per this file's own header on personalized
 * ordering. The admin group (F21) earns the exception: both destinations
 * are server-gated (cloud-api's requireAdmin), so showing them to a COACH
 * would be dead links, not discoverable destinations.
 */
function accountItems(isAdmin: boolean): NavItem[][] {
  const groups: NavItem[][] = [
    [
      { to: '/projects', label: t('Projects') },
      // Unconditional, including for an account with no projects: what the
      // plan gives you and what you have spent of it is answerable before you
      // own an app, and hiding it until you do is how a person concludes
      // there is no meter at all.
      { to: '/usage', label: t('Usage'), tour: 'nav-usage' },
      { to: '/account', label: t('Account'), tour: 'nav-account' },
    ],
  ];
  if (isAdmin) {
    groups.push([
      { to: '/admin/platform', label: t('Platform') },
      { to: '/admin/waitlist', label: t('Wait list') },
      { to: '/admin/god-view', label: t('God View') },
      { to: '/admin/billing', label: t('Plans & credits') },
      { to: '/admin/feedbacks', label: t('Feedbacks') },
    ]);
  }
  return groups;
}

interface ProjectNavProps {
  slug: string | null;
}

export function ProjectNav({ slug }: ProjectNavProps) {
  const account = useAccount();
  const projects = useProjects();
  const project = slug ? projects.find((one) => one.slug === slug) : undefined;
  const groups = slug
    ? projectItems(slug, project?.status === 'provisioning')
    : accountItems(account.isAdmin);

  return (
    <nav className="nav" aria-label={t('Sections')}>
      {groups.map((group, index) => (
        <div className="nav__group" key={group[0]?.to ?? index}>
          {index > 0 ? <div className="nav__rule" role="presentation" /> : null}
          {group.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className="nav__item"
              data-tour={item.tour}
              onClick={() => {
                captureEvent('nav_link_clicked', {
                  to: item.to,
                  label: item.label,
                  project_slug: slug,
                });
              }}
            >
              <span className="nav__label">{item.label}</span>
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}
